import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, EmptyState, ErrorAlert, Field, Modal, PageHeader, Pagination, Spinner } from '../components/ui';
import { CONTENT_GOALS } from '@kontentowo/config';
import { formatDate, truncate } from '@kontentowo/shared';

const STATUS_TONES: Record<string, 'info' | 'warning' | 'success' | 'neutral' | 'danger'> = {
  OPEN: 'info',
  PICKED: 'warning',
  USED: 'success',
  DISCARDED: 'danger',
};

export function IdeasPage() {
  const navigate = useNavigate();
  const [brandId, setBrandId] = useState('');
  const { data: brands } = useAsync(() => api.brands.list(), []);
  const { data, error, loading, reload } = useAsync(() => api.ideas(brandId || undefined), [brandId]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ topic: '', goal: 'AWARENESS', description: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const create = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.ideaCreate({ brandId: brandId || (brands?.[0]?.id ?? ''), topic: form.topic, goal: form.goal, description: form.description || null });
      setOpen(false);
      setForm({ topic: '', goal: 'AWARENESS', description: '' });
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: string, status: string) => {
    setBusyId(id);
    try {
      await api.ideaUpdate(id, { status });
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Usunąć pomysł?')) return;
    setBusyId(id);
    try {
      await api.ideaRemove(id);
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusyId(null);
    }
  };

  const useIdea = (idea: { brandId: string; topic: string; id: string }) => {
    navigate(`/content-studio?brand=${idea.brandId}&topic=${encodeURIComponent(idea.topic)}&idea=${idea.id}`);
  };

  return (
    <>
      <PageHeader title="Ideas" subtitle="Baza pomysłów na treści per marka. Wybrany pomysł otwiera Content Studio." actions={<Button variant="primary" onClick={() => setOpen(true)}>+ Nowy pomysł</Button>} />

      <Card title="Filtruj">
        <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          <option value="">— wszystkie marki —</option>
          {brands?.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </Card>

      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}
      <ErrorAlert error={saveError} />
      {data && data.length === 0 && <EmptyState icon="✎" title="Brak pomysłów" hint="Dodaj pierwszy pomysł lub wygeneruj go później." />}

      {data && data.length > 0 && (
        <Card flush>
          <table className="table">
            <thead>
              <tr>
                <th>Pomysł</th>
                <th>Brand</th>
                <th>Cel</th>
                <th>Status</th>
                <th>Updated</th>
                <th style={{ textAlign: 'right' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {data.slice((page - 1) * 10, page * 10).map((i) => (
                <tr key={i.id}>
                  <td style={{ fontWeight: 550 }}>
                    {truncate(i.topic, 60)}
                    {i.description && <div className="muted" style={{ fontSize: 12 }}>{truncate(i.description, 80)}</div>}
                  </td>
                  <td className="muted">{brands?.find((b) => b.id === i.brandId)?.name ?? i.brandId}</td>
                  <td className="muted">{i.goal ?? '—'}</td>
                  <td>
                    <Badge tone={STATUS_TONES[i.status] ?? 'neutral'}>{i.status}</Badge>
                  </td>
                  <td className="muted">{formatDate(i.updatedAt)}</td>
                  <td>
                    <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                      {i.status !== 'USED' && i.status !== 'DISCARDED' && (
                        <Button size="sm" variant="primary" disabled={busyId === i.id} onClick={() => useIdea(i)}>
                          Wygeneruj
                        </Button>
                      )}
                      {i.status === 'OPEN' && (
                        <Button size="sm" disabled={busyId === i.id} onClick={() => setStatus(i.id, 'PICKED')}>
                          Wybierz
                        </Button>
                      )}
                      {(i.status === 'OPEN' || i.status === 'PICKED') && (
                        <Button size="sm" disabled={busyId === i.id} onClick={() => setStatus(i.id, 'DISCARDED')}>
                          Odrzuć
                        </Button>
                      )}
                      <Button size="sm" variant="danger" disabled={busyId === i.id} onClick={() => remove(i.id)}>
                        Usuń
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={10} total={data.length} onChange={setPage} />
        </Card>
      )}

      <Modal
        title="Nowy pomysł"
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button variant="primary" onClick={create} disabled={saving || !form.topic || (!brandId && !brands?.length)}>
              {saving ? 'Zapisywanie…' : 'Utwórz'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={saveError} />
        <Field label="Marka">
          <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Pomysł">
          <input className="input" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="np. Poradnik: jak zaplanować budżet na rok" />
        </Field>
        <div className="form-row">
          <Field label="Cel">
            <select className="select" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })}>
              {CONTENT_GOALS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Opis">
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
      </Modal>
    </>
  );
}
